import { RUNTIME_UI_SIGNATURE_REFERENCE_PATTERN } from './chunk-DHOJC3F4.js';
import { z } from 'zod';

// src/ui-runtime/harvested-route-grammar.ts
var MAX_HARVESTED_ROUTES = 200;
var MAX_HARVESTED_ROUTE_PATH_CHARS = 200;
var SAFE_PCT_ESCAPE = String.raw`%[89A-Fa-f][0-9A-Fa-f]`;
var PATH_PATTERN = new RegExp(
  String.raw`^/$|^(?:/(?:(?!\.+(?=/|$))(?:[A-Za-z0-9._~!$'(),+-]|${SAFE_PCT_ESCAPE})(?:[A-Za-z0-9._~!$'(),+:-]|${SAFE_PCT_ESCAPE})*|:[A-Za-z0-9_]+|\*))+$`
);
var HARVESTED_ROUTE_PATH_PATTERN = PATH_PATTERN;
var HARVESTED_ACTION_PATH_PATTERN = PATH_PATTERN;

// src/ui-runtime/harvested-form-vocabulary.ts
var HARVESTED_FIELD_INPUT_TYPES = [
  "text",
  "search",
  "email",
  "url",
  "tel",
  "number",
  "range",
  "date",
  "datetime-local",
  "month",
  "week",
  "time",
  "color",
  "checkbox",
  "radio",
  "file"
];
var HARVESTED_FORM_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];
var HARVESTED_FORM_SOURCES = [
  "live",
  "noscript",
  "htmx",
  "handler",
  "element",
  "manifest",
  "bundle"
];

// src/ui-runtime/harvested-form-grammar.ts
var HARVESTED_FIELD_NAME_PATTERN = /^[\p{L}\p{M}\p{N}_.$:[\]-]{1,64}$/u;
var HARVESTED_ACTION_PATTERN = HARVESTED_ACTION_PATH_PATTERN;

// src/ui-runtime/harvested-form.ts
var MAX_HARVESTED_FORMS = 20;
var MAX_HARVESTED_FORM_FIELDS = 60;
var MAX_HARVESTED_FIELD_ENUM_VALUES = 40;
var MAX_HARVESTED_ENUM_VALUE_CHARS = 120;
var MAX_HARVESTED_FIELD_NAME_CHARS = 64;
var MAX_HARVESTED_ACTION_CHARS = 500;
var MAX_HARVESTED_PATTERN_CHARS = 200;

// src/ui-runtime/runtime-ui-vocabulary.ts
var UI_CONTROL_ROLES = [
  "button",
  "link",
  "textbox",
  "checkbox",
  "radio",
  "switch",
  "select",
  "combobox",
  "slider",
  "tab",
  "menuitem",
  "option",
  /**
   * W2-1 (ADR-0396) — a whole discovered `<form>` (or `role="form"` container, or
   * framework pseudo-form) as ONE governable unit, distinct from the individual
   * controls inside it. `computeRole` never returns this from a single element:
   * it is minted by the form harvest, which groups fields into a unit and reports
   * the unit alongside its members.
   *
   * The point of putting it in this vocabulary rather than inventing a parallel
   * one is governance reuse — a `form` row lands in `registered_ui_controls` like
   * any other control, so the operator's existing per-control DENY switches off
   * the whole form, and `screen-ui-scan` drops it before the model sees it.
   */
  "form",
  /**
   * W2-4 (ADR-0396) — a route the host page's FRAMEWORK MANIFEST declares, as one
   * governable unit. Like `form`, `computeRole` never returns this from an element:
   * it is minted by the route-manifest harvest, which reads a declaration
   * (`__BUILD_MANIFEST.sortedPages`, a Remix `routes` map) rather than the DOM.
   *
   * The governance-reuse argument is the same as `form`'s and is stronger here. A
   * build-time route list is not authorization-aware, so it enumerates routes the
   * visitor cannot reach — `/admin/users`, `/internal/debug`. Discovering them is
   * the point; giving the operator no way to say "never that one" would not have
   * been shippable, and a `route` row inherits that lever for free.
   */
  "route",
  "other"
];
var UI_CONTROL_RISK_CLASSES = ["read", "nav", "write", "destructive"];
var UI_CONTROL_CONTEXTS = [
  "page",
  "dialog",
  "menu",
  "tabpanel",
  "accordion",
  "popover"
];
var UI_CONTROL_CONSEQUENCES = [
  "navigates",
  "mutates-view",
  "reveals",
  "submits",
  "downloads",
  "external",
  /**
   * ADR-0751 — OBSERVED, never inferred: the sealed pass pressed the control
   * and nothing happened — no request the seal refused, no route pushed, no
   * navigation, nothing revealed, no change to the page's state. The one
   * member of this list a static read can never produce (a `<button>` with no
   * handler and a `<button>` that posts look identical at rest), and the one
   * that lets the page-act gate RUN a control instead of asking: a press that
   * sends nothing is what ADR-0644 designed the gate to let through.
   */
  "silent"
];
var OBSERVED_CONSEQUENCES = ["silent", "mutates-view"];
function isObservedConsequence(value) {
  return OBSERVED_CONSEQUENCES.some((c) => c === value);
}
var UI_CONTROL_TIERS = ["reads", "writes", "destructive"];
function uiControlTierRank(tier) {
  return UI_CONTROL_TIERS.indexOf(tier);
}
var MAX_RUNTIME_UI_CONTROLS = 300;
var MAX_RUNTIME_UI_SECTIONS = 60;
var MAX_RUNTIME_UI_NAME_CHARS = 200;
var MAX_ANCHOR_TEXT_CHARS = 120;
var MAX_RUNTIME_UI_SECTION_LABEL_CHARS = 120;
var MAX_RUNTIME_UI_PAGE_CHARS = 200;
var MAX_RUNTIME_UI_SIGNATURE_CHARS = 40;
var MAX_RUNTIME_UI_HANDLE_CHARS = 64;
var MAX_RUNTIME_UI_COLLECTION_SIZE = 1e8;
var MAX_RUNTIME_UI_OPTIONS = 12;
var MAX_RUNTIME_UI_OPTION_CHARS = 60;
var MATCHING_FEATURE_FIELDS = {
  /**
   * WS-A observation seam — the control's structural fingerprint
   * (`simhashFromTagPath` over the root-to-element tag path), the feature
   * the certified binding threshold requires. 8 hex chars; optional for
   * widgets predating it.
   *
   * Reaches the identity ingest and NOT the model (`toModelRow`, WF-4.30a):
   * at 11.0 % of a scan result's tokens it was the single most expensive
   * field on a surface that addresses a control by opaque `handle`.
   */
  structureSimhash: z.string().regex(/^[0-9a-f]{8}$/).optional(),
  /**
   * PR-0.3 (C4) — the two locale-invariant tie-break features `decideBinding`
   * consumes to disambiguate a rename among structurally-identical siblings.
   * Both are MATCHING features (never an address, never grounding a gate),
   * carried like `structureSimhash`: they reach the IDENTITY INGEST, and are
   * kept off both the persisted inventory (`toInventoryControl`) and the
   * model-facing result (`toModelRow`). This block used to say they reached
   * the model too, "a harmless machine value" — harmless and not free:
   * measured over 245 controls they were 20.6 % of every `ui_scan` result's
   * tokens, for values nothing model-side reads (WF-4.30a). Optional so a
   * widget predating them validates
   * unchanged — `decideBinding` maps an absent ordinal to -1 and an empty
   * `wireName` to "no wire-name signal".
   *
   * `wireName` is the form-control wire name (submitted key), REFUSED against
   * the SAME closed grammar the W2-1 form harvest uses (`HARVESTED_FIELD_NAME_
   * PATTERN`, ADR-0398). `.catch(undefined)` drops the FIELD on a grammar
   * miss rather than the ROW — a control with an odd `name` must still ground
   * and still bind, and `wireName` only ever enters an EQUALITY comparison, so
   * an absent one loses a matching signal and can never mislead one.
   */
  wireName: z.string().max(MAX_HARVESTED_FIELD_NAME_CHARS).regex(HARVESTED_FIELD_NAME_PATTERN).optional().catch(void 0),
  /**
   * PR-0.3 (C4) — the document-order index within the observation's SimHash
   * group (the structural-group ordinal `decideBinding`'s rule-5 tie-break
   * reads). Non-negative when known; `.catch(undefined)` maps the widget's
   * "unknown" sentinel (-1) and any out-of-range value to an absent field,
   * which `decideBinding` reads back as -1 (never matches — safe).
   */
  siblingOrdinal: z.number().int().nonnegative().max(MAX_RUNTIME_UI_CONTROLS).optional().catch(void 0),
  /**
   * WF-4.53 (D23) — the NON-POSITIONAL discriminator `decideBinding` requires
   * before `siblingOrdinal` may bind. The row's identifying text
   * ("Invoice #4470"), not the control's own ("Delete"), because a value the
   * whole tie shares vouches for nobody.
   *
   * A MATCHING FEATURE, in the same class as `structureSimhash` and
   * `wireName`: kept OFF the persisted inventory descriptor, never rendered,
   * never a label, never sent to a model. It is host-page prose and therefore
   * attacker-influenceable, so it crosses the wire raw-but-bounded and is
   * screened server-side at the W5-1 chokepoint like every other harvested
   * string — the same treatment, and for the same reason, as a form label.
   *
   * **"Never sent to a model" was a claim rather than a control until
   * WF-4.30a.** `sanitizeRow` kept the field, so it rode `result.rows` on
   * every scan of a page with structural sibling groups — and on THAT path it
   * passed neither `cleanObservedName` nor `sanitizeToolText`, because the
   * W5-1 screening this paragraph credits happens in the identity ingest, one
   * consumer over. `toModelRow` drops it, which is what the sentence always
   * described; screening it instead would have kept paying tokens for a field
   * with no model-side reader.
   *
   * `.catch(undefined)` drops the FIELD rather than the row: an absent anchor
   * loses a matching signal, which refuses the ordinal, which is the safe
   * direction. It can never mislead one, because the rule requires agreement
   * AND uniqueness rather than treating absence as either.
   */
  anchorText: z.string().max(MAX_ANCHOR_TEXT_CHARS).optional().catch(void 0)
};
var MATCHING_FEATURE_KEYS = Object.keys(
  MATCHING_FEATURE_FIELDS
);
var RuntimeUiCoverageGapsSchema = /* @__PURE__ */ z.object({
  /** `<iframe>` / `<frame>` elements whose document was cross-origin (never entered). */
  skippedCrossOriginFrames: z.number().int().nonnegative(),
  /** Subtrees pruned because `display:none` removed them from render. */
  skippedDisplayNoneSubtrees: z.number().int().nonnegative(),
  /** `<canvas>` elements seen — pixel regions the walk cannot read into controls. */
  canvasRegionsSeen: z.number().int().nonnegative(),
  /**
   * A conservative upper bound on registered custom elements that expose no
   * walkable content (no open/registered shadow root, no light children) —
   * the strongest page-realm heuristic for an un-exposed CLOSED shadow root,
   * which leaves no reliable trace. Over-counts a genuinely-empty leaf
   * component; documented as a "may be blind here" signal, not a certainty.
   */
  closedRootsUnexposed: z.number().int().nonnegative(),
  /** The collection cap (default 300 controls) was hit before filtering. */
  collectionCapped: z.boolean(),
  /** Total DOM nodes the walk visited — the count `walkOperable` used to discard. */
  walkNodeCount: z.number().int().nonnegative(),
  /** The walk's node cap was reached, so the traversal itself was truncated. */
  walkCapped: z.boolean(),
  /**
   * WF-4.74 (`DEBT.md` D-127) — controls the scan SAW and could not NAME.
   *
   * The same honesty discipline `unnamedFields` applies one harvest over, and
   * it closes the same blind spot from the same side: a scan that quietly
   * returns two blank names reads as a complete scan of a simpler page, and an
   * operator has no way to tell that from a page whose controls are all
   * labelled. A COUNT and never the controls, like every other field here.
   *
   * OPTIONAL on the wire because a producer older than the field is a real case
   * — the CDN serves one artifact but a loaded page holds its own copy — and
   * this is a DISCLOSURE rather than a gate, so absent-means-unreported is the
   * honest reading. Deliberately not the pattern for a machine value that
   * drives a decision, which is refused rather than defaulted (ADR-0398).
   */
  unnamedControls: z.number().int().nonnegative().optional(),
  /**
   * WF-4.23 — features that EXIST and this session could not reach, because a
   * feature flag evaluated off.
   *
   * The only member of this report that is not about the walk's own blindness:
   * every field above says "the scan could not SEE here", and this one says
   * "the page could not SHOW this to this visitor". Both answer the same
   * operator question — is what I am looking at all of it — which is why it
   * rides here rather than becoming a leg of its own.
   *
   * A COUNT, never a key: a flag key is the customer's unreleased product
   * vocabulary (`internal-pricing-experiment-q4` names something they have not
   * announced), and § 2's values line already forbids the evaluated result.
   * Targeting attributes are never read at all.
   *
   * OPTIONAL, and here the absence carries MORE than it does above: a site
   * with no flag SDK and a site whose flags all evaluated ON both have zero
   * hidden features, and only the second is something we measured. Absent
   * means no flag surface was found; `0` means one was, and it hid nothing.
   */
  featuresBehindFlags: z.number().int().nonnegative().optional(),
  /**
   * WF-4.24a — API calls the host application made BEFORE the widget could
   * observe them.
   *
   * The second member here that is not about the walk's own blindness, and it
   * measures a different kind of miss again: `featuresBehindFlags` is surface
   * the page would not SHOW, and this is surface it showed to itself before we
   * arrived. Both answer the operator's one question — is what I am looking at
   * all of it.
   *
   * Its other job is to make a build/no-build decision answerable. WF-4.24's
   * remaining half is a `fetch` tap that outlives a turn on a visitor's live
   * page, which needs its own ADR and a security and legal pass; this number is
   * the evidence for whether that earns its risk on a given tenant, and it is
   * obtained by reading the browser's own resource timing rather than by
   * patching anything.
   *
   * A COUNT, never a URL: a resource-timing entry carries one, and the producer
   * reads `initiatorType` alone, so there is no field a path could travel in.
   *
   * OPTIONAL, with the same absent-is-not-zero contract as the field above:
   * absent means the page exposes no resource timing so we could not look,
   * where `0` means we looked and the app had issued no API call yet — the
   * widget really did load early enough.
   */
  missedApiCallsBeforeBoot: z.number().int().nonnegative().optional(),
  /**
   * WF-4.21b — a data layer is on this page and did NOT expose itself.
   *
   * Measured against `@apollo/client@3.14`: `__APOLLO_CLIENT__` is set only by
   * `connectToDevTools()`, which runs only when `devtools.enabled` — defaulting
   * to `__DEV__ !== false`. So a production Apollo app exposes nothing unless
   * its developer opted in, and the interesting answer is the negative one.
   *
   * A three-state field expressed as an optional boolean, and the shape is the
   * point: ABSENT means no data layer was detected at all, `true` means one was
   * detected and declined, `false` means one was detected and answered. Folding
   * the middle into the first is what makes a coverage number unbelievable —
   * only "we looked and the stack declined" has a remedy the operator can act
   * on, which is one line in their own build.
   */
  dataLayerUnexposed: z.boolean().optional(),
  /**
   * WF-4.49c-iii (A6) — distinct hosts the page's OWN CSP declares it may reach,
   * across `connect-src` and `form-action`.
   *
   * A DENOMINATOR rather than a gap: the server knows how many distinct backends
   * discovery found, and the difference is a sentence an operator can act on —
   * *your own policy names two backends we have not found*. Written by the
   * customer's security team in a language that has no locale, so it is a bound
   * no vocabulary could reach.
   *
   * ABSENT means we could not tell, and that is the common case rather than an
   * edge one: a CSP is normally a response HEADER, and JavaScript cannot read a
   * response header of its own document. Only a `<meta http-equiv>` is legible.
   * Reporting absence as zero would turn an unreadable bound into a claim that
   * the page declares none, on most sites.
   *
   * A count, never the hosts. A CSP names the customer's payment processor and
   * their internal subdomains — their infrastructure map rather than their
   * product's shape — and the count is the whole denominator.
   */
  declaredEgressHosts: z.number().int().nonnegative().optional(),
  /**
   * WF-4.49c-iii — a `'none'` egress directive, which forbids the act outright.
   *
   * Its own field because `form-action 'none'` means every form on the page is
   * inert, and a zero host count says nothing like that: one is "no external
   * destination", the other is "no destination at all". A harvest that proposed
   * those forms as endpoints would be proposing capabilities the page's own
   * policy refuses.
   */
  egressForbidden: z.boolean().optional(),
  /**
   * WF-4.49e-i — `<template>` elements the walk reached and declined to enter.
   *
   * Its own field rather than a term in `skippedDisplayNoneSubtrees`, because a
   * template is not an author-hidden drawer: it is inert BY DEFINITION and
   * exists to be cloned, so on a client-templating stack (Alpine's
   * `<template x-for>`, a `<template id>` cloned into a shadow root) its
   * buttons are on every row of a table the visitor is looking at. Folding it
   * into the drawer count would say the wrong thing about the page; leaving it
   * out entirely said nothing at all, which was worse — such a product reported
   * a near-empty inventory AND zero blind spots, two signals agreeing with each
   * other and both wrong.
   *
   * ABSENT means none were seen, which is the same answer as zero here (every
   * walk looks) — so it is omitted rather than sent as a zero on every page.
   * That is the opposite of `featuresBehindFlags` one field up, where absence
   * and zero are genuinely different answers.
   *
   * A COUNT and nothing more. Admitting the CONTENT needs a third reachability
   * state on `UiScanRow`, since the row carries one `hidden` bit and a consumer
   * cannot otherwise tell "inside a closed modal, one click away" from "inside
   * a template, possibly never instantiated" — a decision rather than an
   * implementation, specced in `plan/walk-first/lanes/LANE-3.md` § WF-4.49e.
   */
  templatesSeen: z.number().int().nonnegative().optional()
}).strict();
var RuntimeUiControlSchema = /* @__PURE__ */ z.object({
  controlId: z.string().min(1).max(MAX_RUNTIME_UI_SIGNATURE_CHARS),
  /**
   * WF-4.50 — the id this control HAD before the section formula saw a labelled
   * `role="group"`. Present only while the two formulas diverge, which is only
   * on a control that gained a section.
   *
   * `HarvestedForm.legacyFormId`'s terms exactly, and for the same reason: the
   * section feeds `computeControlSignature`, so making the walk ARIA-correct
   * re-mints the id of every control inside a labelled group, and every
   * operator deny keyed on the old id would be stranded. The server uses this
   * for ONE thing — carrying that deny onto the new id — and never as a key for
   * anything new. Per-render and temporary; removable once the transition is
   * done.
   */
  legacyControlId: z.string().max(MAX_RUNTIME_UI_SIGNATURE_CHARS).optional(),
  role: z.enum(UI_CONTROL_ROLES),
  name: z.string().max(MAX_RUNTIME_UI_NAME_CHARS),
  section: z.string().max(MAX_RUNTIME_UI_SECTION_LABEL_CHARS).optional(),
  sectionId: z.string().max(MAX_RUNTIME_UI_SIGNATURE_CHARS).optional(),
  page: z.string().max(MAX_RUNTIME_UI_PAGE_CHARS).optional(),
  riskClass: z.enum(UI_CONTROL_RISK_CLASSES),
  /**
   * WS-B1 — which kind of surface contains the control (the precondition
   * model's containment half). Machine-derived (role/aria-modal/<dialog>/
   * <details>/[popover]), so ×6-correct with nothing to localize; optional so
   * a widget predating it validates unchanged. Vocabulary is the shared
   * {@link UI_CONTROL_CONTEXTS} SSOT the widget producer also uses.
   */
  context: z.enum(UI_CONTROL_CONTEXTS).optional(),
  /**
   * WS-B1 — what using the control does (navigation/reveal/submit/…),
   * machine-derived at scan time from the shared
   * {@link UI_CONTROL_CONSEQUENCES} SSOT; optional so a widget predating it
   * validates unchanged. Annotation only — never consulted by a gate.
   */
  consequence: z.enum(UI_CONTROL_CONSEQUENCES).optional(),
  ...MATCHING_FEATURE_FIELDS,
  /**
   * PR-3.2 (M9) — the TRUE size of a virtualized collection this control
   * belongs to, from the one signal that survives virtualization: WAI-ARIA
   * `aria-setsize` (a set member's total) or `aria-rowcount` (a grid's total
   * rows). A virtualized list renders ~14 of 10,000, so the rendered count is
   * wrong by up to 909×; this carries the count the author knows. Grounding
   * only (a machine number the model reads, never a gate), kept OFF the
   * persisted inventory (`toInventoryControl`) like the tie-break features.
   * `.catch(undefined)` drops the FIELD on a bad value, never the row.
   */
  collectionSize: z.number().int().nonnegative().max(MAX_RUNTIME_UI_COLLECTION_SIZE).optional().catch(void 0),
  /**
   * WS-B1 `revealedBy` — the `controlId` of the control whose activation made
   * this one visible, attributed on the AGENT-ACT seam (the widget's
   * verify-effect delta; visitor-click attribution is deliberately excluded on
   * the always-on-observer perf boundary). Grounding only — no gate reads it.
   * A MACHINE value, so it is REFUSED against the signature grammar rather
   * than cleaned (ADR-0398): `computeControlSignature` emits `uic_<base36>`,
   * and anything else is a hand-rolled client inventing a vocabulary.
   *
   * It is refused against the REFERENCE grammar rather than the mint one,
   * because it points at a `controlId` and a `controlId` is re-keyed into
   * the `fh:` namespace when a cross-origin frame's rows are merged into the
   * top document's catalog. Under the mint grammar a frame-qualified
   * reference failed here and the server dropped the WHOLE row — silently,
   * so an in-frame reveal disappeared from grounding, from the identity
   * ingest and from presence, with the widget-side unit test green (agent
   * security review, 2026-08-20). A reference that cannot follow the id it
   * names would name nothing.
   */
  revealedBy: z.string().regex(RUNTIME_UI_SIGNATURE_REFERENCE_PATTERN).optional(),
  /**
   * PR-3.2 (M9) — set when the collection is provably NOT fully rendered (its
   * declared `collectionSize` exceeds what the DOM holds, or it is a grid
   * carrying `aria-rowcount` — the attribute WAI-ARIA defines for rows "not all
   * present in the DOM"). A hint so the agent trusts `collectionSize` over the
   * rows it can see. Optional; a fully-rendered list carries neither field.
   */
  virtualized: z.boolean().optional()
}).strict();
var RuntimeUiSectionSchema = /* @__PURE__ */ z.object({
  sectionId: z.string().min(1).max(MAX_RUNTIME_UI_SIGNATURE_CHARS),
  label: z.string().max(MAX_RUNTIME_UI_SECTION_LABEL_CHARS),
  page: z.string().max(MAX_RUNTIME_UI_PAGE_CHARS).optional()
}).strict();
var RuntimeUiControlsReportSchema = /* @__PURE__ */ z.object({
  controls: z.array(RuntimeUiControlSchema).max(MAX_RUNTIME_UI_CONTROLS),
  sections: z.array(RuntimeUiSectionSchema).max(MAX_RUNTIME_UI_SECTIONS)
}).strict();
var UiControlStateSchema = /* @__PURE__ */ z.object({
  value: z.string().max(MAX_RUNTIME_UI_NAME_CHARS).optional(),
  checked: z.boolean().optional(),
  expanded: z.boolean().optional(),
  selected: z.boolean().optional()
}).strict();
var UiScanRowSchema = RuntimeUiControlSchema.extend({
  handle: z.string().min(1).max(MAX_RUNTIME_UI_HANDLE_CHARS),
  disabled: z.boolean().optional(),
  state: UiControlStateSchema.optional(),
  /**
   * NEXT-LEVEL-PLAN unit F — a `<select>`'s own option labels, so a step can say
   * WHICH one fits rather than naming the control and stopping.
   *
   * The plan's §1.2 complaint restated: "six steps naming five drawer fields is
   * an inventory of a form, not an explanation of a task." A planner that knows
   * a Status filter exists can only write "set the Status filter"; one that
   * knows it currently reads Active and offers Delayed can write the sentence a
   * person would say. This widens what the planner is GIVEN, never what it may
   * do — no gate reads it.
   *
   * It is also what unit D's `perform.value` selects FROM: without this the
   * model would have to guess an option label, and the widget would refuse the
   * guess (`select-options.ts`). Grounding and capability, one field.
   *
   * Redacted on exactly the same terms as `state.value` — a select of saved
   * addresses is as personal as a text input holding one — and BOUNDED, because
   * a country picker has two hundred entries and a tour does not need them.
   */
  options: z.array(z.string().max(MAX_RUNTIME_UI_OPTION_CHARS)).max(MAX_RUNTIME_UI_OPTIONS).optional(),
  /**
   * How many options the select really has, when {@link options} is capped.
   *
   * Present only when it EXCEEDS what was reported, so its presence is the
   * truncation signal and its absence means the list is complete — the
   * `no silent caps` rule (`.claude/rules/quality-bar.md`) on a surface where a
   * silent cap would have the model confidently tell a visitor their status is
   * not on a list of two hundred it saw twelve of.
   */
  optionCount: z.number().int().positive().optional(),
  /**
   * NEXT-LEVEL-PLAN unit F — is this field required?
   *
   * A MACHINE signal (`required` / `aria-required`), so it is ×6-correct with
   * nothing to localize — the rule every derived axis on this schema follows.
   * What it buys a step is the difference between listing five fields and
   * saying which two the visitor cannot skip.
   */
  required: z.boolean().optional()
}).strict();

// src/ui-runtime/harvested-action.ts
var MAX_LD_JSON_SCRIPTS = 12;
var MAX_LD_JSON_BYTES = 64 * 1024;
var MAX_HARVESTED_ACTIONS = 30;
var MAX_HARVESTED_ENTITY_TYPES = 20;
var MAX_HARVESTED_ACTION_PARAMS = 20;
var MAX_HARVESTED_ACTION_VALUE_LENGTH = 1e5;
var MAX_HARVESTED_ACTION_NAME_CHARS = 120;
var HARVESTED_ACTION_PARAM_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
var HARVESTED_ACTION_TYPES = [
  "SearchAction",
  "OrderAction",
  "BuyAction",
  "ReserveAction",
  "SubscribeAction",
  "RegisterAction",
  "CommunicateAction",
  "ReviewAction",
  "ViewAction",
  "DownloadAction"
];
var HARVESTED_ACTION_METHODS = ["GET", "POST"];
var HARVESTED_ENTITY_TYPES = [
  "Product",
  "Offer",
  "Order",
  "Review",
  "Article",
  "BlogPosting",
  "Event",
  "Organization",
  "Person",
  "Place",
  "Recipe",
  "Course",
  "JobPosting",
  "Service",
  "SoftwareApplication",
  "WebSite",
  "WebPage",
  "BreadcrumbList",
  "FAQPage",
  "LocalBusiness"
];

// src/ui-runtime/harvested-anchor.ts
var MAX_HARVESTED_ANCHORS = 60;
var MAX_ANCHOR_ELEMENTS_SCANNED = 2e3;
var MAX_HARVESTED_ANCHOR_COUNT = 1e4;

// src/ui-runtime/harvested-tool.ts
var MAX_HARVESTED_TOOLS = 30;
var MAX_HARVESTED_TOOL_ARGS = 20;
var MAX_HARVESTED_TOOL_DESCRIPTION_CHARS = 400;
var HARVESTED_TOOL_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_.-]{0,63}$/;
var HARVESTED_TOOL_ARG_PATTERN = /^[\p{L}\p{N}_.$:[\]-]{1,64}$/u;

export { HARVESTED_ACTION_METHODS, HARVESTED_ACTION_PARAM_PATTERN, HARVESTED_ACTION_PATTERN, HARVESTED_ACTION_TYPES, HARVESTED_ENTITY_TYPES, HARVESTED_FIELD_INPUT_TYPES, HARVESTED_FIELD_NAME_PATTERN, HARVESTED_FORM_METHODS, HARVESTED_FORM_SOURCES, HARVESTED_ROUTE_PATH_PATTERN, HARVESTED_TOOL_ARG_PATTERN, HARVESTED_TOOL_NAME_PATTERN, MATCHING_FEATURE_KEYS, MAX_ANCHOR_ELEMENTS_SCANNED, MAX_ANCHOR_TEXT_CHARS, MAX_HARVESTED_ACTIONS, MAX_HARVESTED_ACTION_CHARS, MAX_HARVESTED_ACTION_NAME_CHARS, MAX_HARVESTED_ACTION_PARAMS, MAX_HARVESTED_ACTION_VALUE_LENGTH, MAX_HARVESTED_ANCHORS, MAX_HARVESTED_ANCHOR_COUNT, MAX_HARVESTED_ENTITY_TYPES, MAX_HARVESTED_ENUM_VALUE_CHARS, MAX_HARVESTED_FIELD_ENUM_VALUES, MAX_HARVESTED_FIELD_NAME_CHARS, MAX_HARVESTED_FORMS, MAX_HARVESTED_FORM_FIELDS, MAX_HARVESTED_PATTERN_CHARS, MAX_HARVESTED_ROUTES, MAX_HARVESTED_ROUTE_PATH_CHARS, MAX_HARVESTED_TOOLS, MAX_HARVESTED_TOOL_ARGS, MAX_HARVESTED_TOOL_DESCRIPTION_CHARS, MAX_LD_JSON_BYTES, MAX_LD_JSON_SCRIPTS, MAX_RUNTIME_UI_COLLECTION_SIZE, MAX_RUNTIME_UI_CONTROLS, MAX_RUNTIME_UI_HANDLE_CHARS, MAX_RUNTIME_UI_NAME_CHARS, MAX_RUNTIME_UI_OPTIONS, MAX_RUNTIME_UI_OPTION_CHARS, MAX_RUNTIME_UI_PAGE_CHARS, MAX_RUNTIME_UI_SECTIONS, MAX_RUNTIME_UI_SECTION_LABEL_CHARS, MAX_RUNTIME_UI_SIGNATURE_CHARS, OBSERVED_CONSEQUENCES, RuntimeUiControlSchema, RuntimeUiControlsReportSchema, RuntimeUiCoverageGapsSchema, RuntimeUiSectionSchema, UI_CONTROL_CONSEQUENCES, UI_CONTROL_CONTEXTS, UI_CONTROL_RISK_CLASSES, UI_CONTROL_ROLES, UI_CONTROL_TIERS, UiControlStateSchema, UiScanRowSchema, isObservedConsequence, uiControlTierRank };
//# sourceMappingURL=chunk-NOQNWA6J.js.map
//# sourceMappingURL=chunk-NOQNWA6J.js.map